import React, { useState, useContext, useMemo, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useMutation } from "@apollo/client";
import cx from "classnames";
import { Loading } from "@foris/avocado-suite";
import { linkValidation, IObjValidationError, getValidationErrorReason } from "./utils";
import i18n from "@config/i18n";
import {
  IContextVacancies,
  ContextVacancies,
  IGroupValue,
  IGroup,
} from "./contextVacancies/ContextVacancies";
import Footer from "./Footer";
import Links from "./Links/Links";
import { BUNDLES_MUTATION } from "./graphql/bundles.queries";
import {
  Bundle,
  CubeMutation,
  BundleValidationError,
  EditBundlePayload,
} from "../../models/ISchema";
import { IParams } from "../../models/IParams";
import css from "./vacancies.module.scss";
import "react-tabs/style/react-tabs.css";
import { CardNotification, Text } from "@foris/avocado-suite";
import { emitEvent, Events, useListenEvent } from "@common/hooks/useListenEvent";
import useUpdateGroupIsReferent, {
  UpdateGroupIsReferentPayload,
} from "./hooks/useUpdateGroupIsReferent";
import { setAllChangesAreValid } from "./contextVacancies/useVacanciesStore";
import GroupTagsDrawer from "@dimensions/group/components/group-tags-drawer";
import GroupRestrictionsDrawer from "@dimensions/group/components/group-restrictions-drawer";
import { useBundleEditAbilities } from "./hooks/useBundleEditAbilities";

export enum VacanciesType {
  instructor = "instructor",
  vacancies = "vacancies",
}

interface IVacanciesGroup {
  active: boolean;
  setActive: (value: boolean) => void;
  dataBundle: Bundle;
}

const VacanciesGroup: React.FC<IVacanciesGroup> = (props: IVacanciesGroup) => {
  const { active, setActive, dataBundle } = props;
  const { origin, scenario, id }: IParams = useParams();
  const [vacanciesMutation] = useMutation(BUNDLES_MUTATION, {});
  const [loading, setLoading] = useState(false);
  const context: IContextVacancies = useContext(ContextVacancies);
  const { canEnterVacanciesEdit } = useBundleEditAbilities(dataBundle);
  const [validationArray, setValidationArray] = useState(null);
  const [disabledConfirm, setDisabledConfirm] = useState(true);
  const [groupsReferentData, setGroupsReferentData] = useState<{
    [linkId: string]: UpdateGroupIsReferentPayload;
  }>({});
  const [selectedGroup, setSelectedGroup] = useState<IGroup | null>(null);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [showTagsDrawer, setShowTagsDrawer] = useState(false);
  const [showRestrictionsDrawer, setShowRestrictionsDrawer] = useState(false);

  useListenEvent({
    eventKey: "on:update",
    handler: (detail: Events["on:update"]) => {
      if (detail?.target === "group-referent") {
        setGroupsReferentData(prev => ({
          ...prev,
          [detail.data.linkId]: {
            groupId: detail.data.groupId,
            isReferent: detail.data.isReferent,
          },
        }));
      }
    },
  });

  useListenEvent({
    eventKey: "on:update",
    handler: (detail: Events["on:update"]) => {
      if (detail?.target === "open-group-tags-drawer") {
        setSelectedGroup(detail?.data?.group);
        setShowTagsDrawer(true);
      }

      if (detail?.target === "open-group-restrictions-drawer") {
        setSelectedGroupId(detail?.data?.groupId);
        setShowRestrictionsDrawer(true);
      }
    },
  });

  const warningArray = useMemo(
    () =>
      validationArray
        ? [...validationArray].filter(
            (validation: IObjValidationError) => validation.type === "warning",
          )
        : [],
    [validationArray],
  );

  const groupsReferentPayload = useMemo(() => {
    return Object.values(groupsReferentData).map(group => ({
      groupId: group.groupId,
      isReferent: group.isReferent,
    }));
  }, [groupsReferentData]);

  const [{ isLoading: isLoadingUpdateGroupIsReferent }, updateGroupIsReferent] =
    useUpdateGroupIsReferent({
      onSuccess: () => {
        setGroupsReferentData({});
        setActive(false);
        emitEvent("on:refresh", {
          target: "bundle-view",
        });
      },
    });

  const changeVacanciesPayload = useMemo(() => {
    const changeVacancies = [];

    (context?.currentLeague ?? []).forEach((league, linkIndex) => {
      const linkGroups = context?.changeValues?.[linkIndex] ?? [];
      const retakerLinkGroups = context?.retakerChangeValues?.[linkIndex] ?? [];

      const objLink = {
        linkId: parseInt(league.linkId),
        changeGroupVacancies: [],
      };

      (league.groups ?? []).forEach(leagueGroup => {
        const vacancyGroup = linkGroups.find((g: IGroupValue) => g.group.id === leagueGroup.id);
        const retakerGroup = retakerLinkGroups.find(
          (r: IGroupValue) => r.group.id === leagueGroup.id,
        );

        const currentVacancies = vacancyGroup?.value ?? leagueGroup.vacancies;
        const vacanciesChanged = +leagueGroup.vacancies !== +currentVacancies;

        const originalRetaker = leagueGroup.groupCapacity?.retakerCapacity ?? 0;
        const retakerRaw = retakerGroup?.value;
        const retakerVacancies =
          retakerRaw !== undefined && retakerRaw !== null && !isNaN(Number(retakerRaw))
            ? Number(retakerRaw)
            : null;
        const retakerChanged = retakerVacancies !== null && retakerVacancies !== originalRetaker;

        const groupChanges: { vacancies?: number; retakerVacancies?: number | null } = {
          vacancies: currentVacancies,
        };

        if (retakerChanged) {
          groupChanges.retakerVacancies = retakerVacancies;
        }

        if (retakerChanged || vacanciesChanged) {
          objLink.changeGroupVacancies.push({
            groupId: parseInt(leagueGroup.id),
            ...groupChanges,
          });
        }
      });

      if (objLink.changeGroupVacancies.length > 0) {
        changeVacancies.push(objLink);
      }
    });

    return changeVacancies;
  }, [context.currentLeague, context.changeValues, context.retakerChangeValues]);

  const handleOnSaveVacancies = async (commit: boolean) => {
    try {
      setLoading(true);

      const objMutation = {
        scenarioId: parseInt(scenario),
        originId: parseInt(origin),
        bundleInput: {
          bundleId: parseInt(id),
          dryRun: !commit,
          skipValidations: commit,
          changeset: {
            changeVacancies: changeVacanciesPayload,
          },
        },
      };
      if (!commit && changeVacanciesPayload.length > 0) {
        setLoading(false);
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        await callMutationHook(objMutation);
      } else if (changeVacanciesPayload.length > 0) {
        setActive(true);
        const data = await vacanciesMutation({ variables: objMutation });
        const dataMutation: EditBundlePayload = data.data.cube.editBundle;
        if (dataMutation.commited) {
          setLoading(false);
          setActive(false);
          window.location.reload();
        }
      }
      setLoading(false);
    } catch (error) {
      console.log(error);
    }
  };

  const callMutationHook = async (variables: any) => {
    try {
      setLoading(true);
      const data = await vacanciesMutation({ variables });
      const dataMutation: CubeMutation = data.data.cube;
      const validationErrors: Array<BundleValidationError> =
        dataMutation.editBundle.validationErrors;
      let errorsValidations = [];

      if (validationErrors && validationErrors.length > 0) {
        validationErrors.forEach((error: BundleValidationError) => {
          const errorLinks = linkValidation(error);
          errorsValidations = [...errorsValidations, ...errorLinks];
        });

        setValidationArray(errorsValidations);
      } else {
        handleOnSaveVacancies(true);
      }

      setLoading(false);
    } catch (e) {
      console.error(e);
      setLoading(false);
    }
  };

  const retakerValidationErrors = useMemo(() => {
    const errors: IObjValidationError[] = [];

    (context?.currentLeague ?? []).forEach((league, linkIndex) => {
      const linkGroups = context?.changeValues?.[linkIndex] ?? [];
      const retakerLinkGroups = context?.retakerChangeValues?.[linkIndex] ?? [];

      (league.groups ?? []).forEach(leagueGroup => {
        const vacancyGroup = linkGroups.find((g: IGroupValue) => g.group.id === leagueGroup.id);
        const retakerGroup = retakerLinkGroups.find(
          (r: IGroupValue) => r.group.id === leagueGroup.id,
        );

        const currentVacancies = vacancyGroup?.value ?? leagueGroup.vacancies;
        const retakerRaw = retakerGroup?.value ?? leagueGroup.groupCapacity?.retakerCapacity;
        const currentRetaker =
          retakerRaw !== undefined && retakerRaw !== null && !isNaN(Number(retakerRaw))
            ? Number(retakerRaw)
            : null;

        if (currentRetaker !== null && currentRetaker > currentVacancies) {
          errors.push({
            type: "error",
            text: i18n.t("bundle.errors.retaker-capacity-exceeds-vacancies"),
            leagueCode: league.code,
          });
        }
      });
    });

    return errors;
  }, [context.currentLeague, context.changeValues, context.retakerChangeValues]);

  const handleUpdateGroups = (commit: boolean) => {
    if (isLoadingUpdateGroupIsReferent || loading) return;

    if (retakerValidationErrors.length > 0) {
      setValidationArray(retakerValidationErrors);
      return;
    }

    if (changeVacanciesPayload.length) {
      handleOnSaveVacancies(commit);
    }

    if (groupsReferentPayload.length) {
      updateGroupIsReferent(groupsReferentPayload);
    }
  };

  const validationsByLeagueCode = useMemo(() => {
    const linkCodes = {};

    [...(validationArray ?? []), ...(retakerValidationErrors ?? [])]?.forEach(
      (validation: IObjValidationError) => {
        if (validation.leagueCode) {
          const codeObj = {
            leagueCode: validation.leagueCode,
            errors: [],
            warnings: [],
          };

          const errorOrWarning =
            "text" in validation
              ? validation
              : getValidationErrorReason((validation as any).reason);
          const adaptedValidation = {
            ...(errorOrWarning ?? {}),
            leagueCode: validation.leagueCode,
          };

          if (adaptedValidation?.type === "error") {
            codeObj.errors.push(adaptedValidation);
          } else {
            codeObj.warnings.push(adaptedValidation);
          }

          linkCodes[adaptedValidation?.leagueCode] = {
            ...(linkCodes[adaptedValidation?.leagueCode] || {}),
            errors: [
              ...(linkCodes[adaptedValidation?.leagueCode]?.errors || []),
              ...codeObj.errors,
            ],
            warnings: [
              ...(linkCodes[adaptedValidation?.leagueCode]?.warnings || []),
              ...codeObj.warnings,
            ],
          };
        }
      },
    );

    return linkCodes;
  }, [validationArray, retakerValidationErrors]);

  useEffect(() => {
    const hasErrors = Object.values(validationsByLeagueCode).some(
      (league: any) => league.errors?.length > 0,
    );
    setAllChangesAreValid(!hasErrors);

    return () => {
      setAllChangesAreValid(true);
    };
  }, [validationsByLeagueCode]);

  return (
    <div>
      {(loading || isLoadingUpdateGroupIsReferent) && <Loading />}
      <section className={cx(css.content, "container-row")}>
        <Links
          nameGroup={`${dataBundle.code} ${dataBundle.name}`}
          bundleId={dataBundle?.id}
          type={VacanciesType.vacancies}
          data={context.currentLeague}
          activeEdit={active}
          setValidationArray={setValidationArray}
        />
      </section>

      {Object.keys(validationsByLeagueCode).map((leagueCode: string) => {
        const { errors, warnings } = validationsByLeagueCode[leagueCode];

        return (
          <>
            {warnings?.length > 0 && (
              <CardNotification
                className={css.notificationCard}
                key={`warning-${leagueCode}`}
                title={`${warnings?.length} Advertencias en liga ${leagueCode}`}
                state="warning"
                outlined
              >
                <div className={css.notificationCard_content}>
                  {warnings.map((warning: IObjValidationError, i) => (
                    <Text type="sm" key={i}>
                      {warning.text}
                    </Text>
                  ))}

                  {!errors?.length && disabledConfirm && (
                    <button className={css.actionButton} onClick={() => setDisabledConfirm(false)}>
                      Desestimar advertencia
                    </button>
                  )}
                </div>
              </CardNotification>
            )}

            {errors?.length > 0 && (
              <CardNotification
                className={css.notificationCard}
                key={`error-${leagueCode}`}
                title={`${errors?.length} Errores en liga ${leagueCode}`}
                state="error"
                outlined
              >
                <div className={css.notificationCard_content}>
                  {errors.map((error: IObjValidationError, i) => (
                    <Text type="sm" key={i}>
                      {error.text}
                    </Text>
                  ))}
                </div>
              </CardNotification>
            )}
          </>
        );
      })}

      <Footer
        activeEdit={active}
        canEnterEdit={canEnterVacanciesEdit}
        setActiveEdit={(value: boolean) => {
          setActive(value);
          setGroupsReferentData({});
        }}
        warningsCount={warningArray.length}
        onSave={handleUpdateGroups}
        disabledConfirm={disabledConfirm}
      />

      <GroupTagsDrawer
        groupId={selectedGroup?.id}
        modalityCode={selectedGroup?.course?.curriculum?.program?.modality?.code}
        isOpen={showTagsDrawer}
        shouldFetchLabels
        onClose={() => {
          setShowTagsDrawer(false);
          setSelectedGroupId(null);
          setSelectedGroup(null);
        }}
      />

      <GroupRestrictionsDrawer
        scenario={scenario}
        origin={origin}
        id={selectedGroupId}
        isOpen={showRestrictionsDrawer}
        onClose={() => {
          setShowRestrictionsDrawer(false);
          setSelectedGroupId(null);
        }}
      />
    </div>
  );
};

export default VacanciesGroup;
